import { TypedViewer } from "./_Base";
import { Poly } from "../Poly";
import { ViewerLogoController } from "./utils/logo/ViewerLogoController";
import { TIME_CONTROLLER_UPDATE_TIME_OPTIONS_DEFAULT } from "../scene/utils/TimeController";
import { CoreCameraRendererController } from "../../core/camera/CoreCameraRendererController";
import { CoreCameraPostProcessController } from "../../core/camera/CoreCameraPostProcessController";
import { CoreCameraCSSRendererController } from "../../core/camera/CoreCameraCSSRendererController";
import { CoreCameraControlsController } from "../../core/camera/CoreCameraControlsController";
import { CoreCameraRenderSceneController } from "../../core/camera/CoreCameraRenderSceneController";
const CSS_CLASS = "CoreThreejsViewer";
export class ThreejsViewer extends TypedViewer {
  constructor(options) {
    super(options);
    this._animateMethod = this.animate.bind(this);
    this._onResizeBound = this.onResize.bind(this);
    this._setupFunctions();
  }
  static _canvasIdPrefix() {
    return "ThreejsViewer";
  }
  _setupFunctions() {
    var _a;
    const camera = this.camera();
    const scene = this.scene();
    const canvas = this.canvas();
    const threejsScene = scene.threejsScene();
    this._renderer = CoreCameraRendererController.createRenderer({
      camera,
      scene,
      canvas
    });
    const renderer = this._renderer;
    if (renderer) {
      this._rendererScene = CoreCameraRenderSceneController.renderScene({ camera, scene });
      const renderScene = this._rendererScene || threejsScene;
      this._effectComposer = CoreCameraPostProcessController.createComposer({
        camera,
        scene,
        renderScene,
        renderer
      });
      this._cssRendererConfig = CoreCameraCSSRendererController.cssRendererConfig({ scene, camera, canvas });
      this._controlsNode = CoreCameraControlsController.controlsNode({ camera, scene });
      const cssRenderer = (_a = this._cssRendererConfig) == null ? void 0 : _a.cssRenderer;
      this._renderCSSFunc = cssRenderer ? () => cssRenderer.render(renderScene, camera) : void 0;
      const effectComposer = this._effectComposer;
      if (effectComposer) {
        this._renderFunc = (delta) => effectComposer.render(delta);
      } else {
        this._renderFunc = () => {
          renderer.render(renderScene, camera);
        };
      }
    }
  }
  mount(element) {
    var _a, _b, _c;
    super.mount(element);
    (_a = this._domElement) == null ? void 0 : _a.appendChild(this.canvas());
    (_b = this._domElement) == null ? void 0 : _b.classList.add(CSS_CLASS);
    const cssRendererNode = (_c = this._cssRendererConfig) == null ? void 0 : _c.cssRendererNode;
    if (cssRendererNode) {
      cssRendererNode.mountRenderer(this.canvas());
    }
    this._build();
    this._setEvents();
    this.onResize();
    if (Poly.logo.displayed()) {
      new ViewerLogoController(this);
    }
  }
  _build() {
    this._initDisplay();
    this.activate();
  }
  dispose() {
    var _a;
    const cssRendererNode = (_a = this._cssRendererConfig) == null ? void 0 : _a.cssRendererNode;
    if (cssRendererNode) {
      cssRendererNode.unmountRenderer(this.canvas());
    }
    this._cssRendererConfig = void 0;
    this._effectComposer = void 0;
    this.setAutoRender(false);
    this._cancelAnimate();
    this._disposeEvents();
    super.dispose();
  }
  _setEvents() {
    this.eventsController().init();
    this.webglController().init();
    this._disposeEvents();
    window.addEventListener("resize", this._onResizeBound, false);
  }
  _disposeEvents() {
    window.removeEventListener("resize", this._onResizeBound, false);
  }
  onResize() {
    var _a;
    const canvas = this._canvas;
    if (!canvas) {
      return;
    }
    this.camerasController().computeSizeAndAspect();
    const size = this.camerasController().size;
    CoreCameraRendererController.setRendererSize(canvas, size);
    (_a = this._cssRendererConfig) == null ? void 0 : _a.cssRenderer.setSize(size.x, size.y);
    this.camerasController().updateCameraAspect();
  }
  _initDisplay() {
    if (!this._canvas) {
      console.warn("no canvas found for viewer");
      return;
    }
    this.camerasController().computeSizeAndAspect();
    this.audioController().update();
    this.animate();
  }
  setAutoRender(state = true) {
    super.setAutoRender(state);
    if (this._doRender) {
      this.animate();
    }
  }
  animate() {
    if (this._doRender) {
      const delta = this._scene.timeController.updateClockDelta();
      this._requestAnimationFrameId = requestAnimationFrame(this._animateMethod);
      this._runOnBeforeTickCallbacks(delta);
      this._scene.timeController.incrementTimeIfPlaying(TIME_CONTROLLER_UPDATE_TIME_OPTIONS_DEFAULT);
      this._runOnAfterTickCallbacks(delta);
      this.render(delta);
    }
  }
  _cancelAnimate() {
    this._doRender = false;
    if (this._requestAnimationFrameId) {
      cancelAnimationFrame(this._requestAnimationFrameId);
    }
    if (this._canvas) {
    }
  }
  render(delta) {
    if (this._canvas) {
      const renderer = this._renderer;
      if (!renderer) {
        return;
      }
      this._runOnBeforeRenderCallbacks(delta, renderer);
      if (this._renderFunc) {
        this._renderFunc(delta);
      }
      if (this._renderCSSFunc) {
        this._renderCSSFunc();
      }
      this.controlsController().update(delta);
      this._runOnAfterRenderCallbacks(delta, renderer);
    } else {
      console.warn("no canvas to render onto");
    }
  }
  renderer() {
    return this._renderer;
  }
  effectComposer() {
    return this._effectComposer;
  }
  preCompile() {
    if (!this._renderer) {
      return;
    }
    this._renderer.compile(this._scene.threejsScene(), this._camera);
  }
  markAsReady() {
    this.preCompile();
    this.setAutoRender(true);
  }
}
